import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PlayCircle,
  Snowflake,
  Activity,
  Sparkles,
  Heart,
  Zap,
  ShieldCheck,
  ArrowRight,
} from 'lucide-react';

const VIDEO_URL =
  'https://customer-assets-v7afamib.emergentagent.net/jobs/0a05bf82-961f-41a0-93d1-2354bed87d8d/videos/535f3cff775544fd.mp4';

const BENEFITS = [
  {
    icon: Activity,
    title: 'Pain Relief',
    body: 'Precisely target sore joints, back, or muscle groups to reduce chronic and acute pain.',
  },
  {
    icon: ShieldCheck,
    title: 'Inflammation',
    body: 'Localized cold constricts blood vessels and calms inflammation in a focused area.',
  },
  {
    icon: Sparkles,
    title: 'Skin Tightening',
    body: 'Improve tone and texture with cold-induced collagen response and micro-circulation.',
  },
  {
    icon: Zap,
    title: 'Muscle Recovery',
    body: 'Post-workout recovery for athletes — faster bounce-back with less soreness.',
  },
  {
    icon: Heart,
    title: 'Aesthetic Treatments',
    body: 'Face, arms, or abdomen aesthetic sessions with the Subzero applicator.',
  },
  {
    icon: Snowflake,
    title: 'Non-Invasive',
    body: 'No downtime, no needles — just deeply chilled, controlled cold air.',
  },
];

export default function HowItWorks() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen frost-bg relative">
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-10">
        {/* Hero */}
        <div className="glass-card rounded-[2.5rem] p-8 sm:p-12 reveal">
          <p className="text-xs tracking-[0.3em] uppercase text-blue-300 font-bold flex items-center gap-2">
            <PlayCircle className="w-4 h-4" />
            How It Works
          </p>
          <h1 className="font-display text-4xl sm:text-6xl text-slate-100 tracking-tight mt-3">
            The American Cryo Subzero System
          </h1>
          <p className="mt-4 text-lg text-slate-300 max-w-3xl leading-relaxed">
            Localized cold therapy powered by the Subzero applicator delivers
            precisely-cooled air to a targeted area of the body. See it in
            action below.
          </p>
        </div>

        {/* Video */}
        <div
          className="mt-8 glass-card rounded-3xl p-3 sm:p-4 reveal reveal-delay-1"
          data-testid="how-video-wrapper"
        >
          <div className="relative rounded-2xl overflow-hidden bg-slate-900 aspect-video">
            <video
              src={VIDEO_URL}
              controls
              playsInline
              preload="metadata"
              className="w-full h-full object-contain bg-black"
              data-testid="how-video-player"
            >
              Your browser does not support the video tag. You can{' '}
              <a
                href={VIDEO_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 underline"
              >
                download it here
              </a>
              .
            </video>
          </div>
          <p className="text-xs text-slate-400 mt-3 px-2">
            Tap the video to play. Full-screen supported on all devices.
          </p>
        </div>

        {/* Description */}
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 glass-card rounded-3xl p-8 reveal reveal-delay-2">
            <h2 className="font-display text-3xl text-slate-100 tracking-tight">
              About Subzero Localized Cryo
            </h2>
            <div className="mt-4 space-y-4 text-[15px] leading-relaxed text-slate-200">
              <p>
                The Subzero system generates a controlled stream of ultra-cold
                air (as low as -220°F) directed at a specific treatment area.
                Sessions are typically short — 5 to 15 minutes — and are
                comfortable for most clients.
              </p>
              <p>
                Because the treatment is <strong>localized</strong>, therapists
                can focus on exactly the area you need: a knee, a shoulder, the
                lower back, the abdomen, or a specific patch of skin for
                aesthetic work.
              </p>
              <p>
                KRYOTEKS specializes in two focused categories:{' '}
                <strong>Aesthetics</strong> (glow, tone, fat reduction) and{' '}
                <strong>Orthopedic</strong> (recovery, joint pain, inflammation).
                Session packages unlock progressive discounts, and referring
                friends earns a lifetime commission on their spending.
              </p>
            </div>
          </div>

          <div className="glass-card rounded-3xl p-8 reveal reveal-delay-3">
            <h3 className="font-display text-xl text-slate-100 tracking-tight">
              Ideal for
            </h3>
            <ul className="mt-4 space-y-3 text-sm text-slate-200">
              {['Athletes & active professionals', 'Chronic joint and back pain', 'Post-workout recovery', 'Skin toning and tightening', 'Localized cellulite reduction', 'Facial aesthetics'].map((item) => (
                <li key={item} className="flex items-start gap-2.5">
                  <Snowflake className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" strokeWidth={1.6} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Benefits grid */}
        <section className="mt-10 reveal reveal-delay-3">
          <h2 className="font-display text-3xl text-slate-100 tracking-tight mb-5 px-2">
            Key Benefits
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {BENEFITS.map((b) => {
              const Icon = b.icon;
              return (
                <div
                  key={b.title}
                  className="glass-card rounded-3xl p-6"
                  data-testid={`how-benefit-${b.title.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="w-11 h-11 rounded-2xl bg-blue-500/20/70 border border-blue-500/30 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-blue-400" strokeWidth={1.6} />
                  </div>
                  <h3 className="font-display text-xl text-slate-100 mt-4">
                    {b.title}
                  </h3>
                  <p className="text-sm text-slate-300 mt-2 leading-relaxed">
                    {b.body}
                  </p>
                </div>
              );
            })}
          </div>
        </section>

        {/* CTA */}
        <div className="mt-12 mb-8 flex flex-col sm:flex-row items-center justify-center gap-4 reveal">
          <button
            onClick={() => navigate('/waiver')}
            className="pill-btn inline-flex items-center gap-3 h-16 px-10 rounded-full bg-blue-500 hover:bg-blue-500 text-white text-lg font-bold shadow-xl shadow-blue-500/30"
            data-testid="how-sign-waiver-button"
          >
            Sign Waiver to Book
            <ArrowRight className="w-5 h-5" />
          </button>
          <button
            onClick={() => navigate('/services')}
            className="pill-btn inline-flex items-center gap-3 h-16 px-10 rounded-full bg-slate-900/70 border-2 border-blue-500/30 text-blue-300 text-lg font-bold hover:bg-slate-800 hover:border-blue-400"
            data-testid="how-view-services-button"
          >
            View Services &amp; Pricing
          </button>
        </div>
      </div>
    </div>
  );
}
